/**
 * Payee contacts and the payout statement.
 *
 * Two halves of one question: who your payees are and how they want to be
 * paid, and what each of them is owed right now. A payee with no contact
 * record still appears on the statement, flagged — that is precisely the one
 * you will be chasing after the event.
 *
 * Payment handles are for a Venmo username or who a check is made out to.
 * The server rejects anything resembling an account or card number.
 */

import { useCallback, useEffect, useState } from 'react';

import * as api from '../lib/api';
import { useInventory } from '../lib/useInventory';

const EMPTY: api.Contact = {
  name: '',
  kind: 'individual',
  payment_method: 'check',
  payment_handle: '',
  email: '',
  phone: '',
  address: '',
  notes: '',
};

/** What to ask for in the handle field, per method. */
const HANDLE_PROMPTS: Record<string, string> = {
  check: 'Make check payable to',
  cash: 'Note (optional)',
  venmo: 'Venmo username, e.g. @alice',
  paypal: 'PayPal email or @handle',
  zelle: 'Zelle email or phone',
  cashapp: 'Cash App $cashtag',
  wire: 'Reference (arrange details separately)',
  store_credit: 'Note (optional)',
  other: 'How to pay them',
};

function methodLabel(value: string): string {
  return api.PAYMENT_METHODS.find(([key]) => key === value)?.[1] ?? value;
}

export default function ContactsPage() {
  const { db, setDb, databases } = useInventory();
  const [contacts, setContacts] = useState<api.Contact[]>([]);
  const [payouts, setPayouts] = useState<api.PayoutRow[]>([]);
  const [total, setTotal] = useState(0);
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');

  const [draft, setDraft] = useState<api.Contact>(EMPTY);
  const [editingName, setEditingName] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setContacts((await api.listContacts()).contacts);
    } catch (err) {
      setMessage((err as api.ApiError).message);
    }
    try {
      const data = await api.payoutStatement(db, { since, until });
      setPayouts(data.payees);
      setTotal(data.total);
    } catch (err) {
      setMessage((err as api.ApiError).message);
    }
  }, [db, since, until]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>, success: string) {
    setBusy(true);
    setMessage('');
    try {
      await action();
      setMessage(success);
      await load();
    } catch (err) {
      setMessage((err as api.ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  function edit(contact: api.Contact) {
    setDraft({ ...contact });
    setEditingName(contact.name);
    setMessage('');
  }

  function startNew() {
    setDraft(EMPTY);
    setEditingName(null);
    setMessage('');
  }

  /** Download the statement as a CSV, without a round trip to the server. */
  function exportCsv() {
    const header = [
      'Payee', 'Type', 'Amount Owed', 'Share %', 'Units', 'Sale Lines',
      'Payment Method', 'Payment Handle', 'Email', 'Phone', 'Address', 'Notes',
    ];
    const escape = (value: unknown) => {
      const text = String(value ?? '');
      // Quote anything a spreadsheet would otherwise mis-split.
      return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const lines = [
      header.join(','),
      ...payouts.map((row) =>
        [
          row.payee, row.kind, row.revenue.toFixed(2), row.share.toFixed(1),
          row.units, row.sales, row.payment_method, row.payment_handle,
          row.email, row.phone, row.address, row.notes,
        ]
          .map(escape)
          .join(','),
      ),
      '',
      ['Total', '', total.toFixed(2)].join(','),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'payouts.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  }

  const missing = payouts.filter((row) => !row.has_contact).length;

  return (
    <div className="page">
      {message && <p className="notice">{message}</p>}

      <section className="card">
        <h2>Payout statement</h2>
        <div className="field-row">
          <label>
            Database
            <select value={db} onChange={(event) => setDb(event.target.value)}>
              {databases.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label>
            From
            <input
              type="date"
              value={since}
              onChange={(event) => setSince(event.target.value)}
            />
          </label>
          <label>
            To
            <input
              type="date"
              value={until}
              onChange={(event) => setUntil(event.target.value)}
            />
          </label>
          <button type="button" onClick={() => void load()} disabled={busy}>
            Refresh
          </button>
          <button
            type="button"
            className="primary"
            onClick={exportCsv}
            disabled={payouts.length === 0}
          >
            Export CSV
          </button>
        </div>

        <p className="total">
          Total owed: ${total.toFixed(2)} across {payouts.length} payee
          {payouts.length === 1 ? '' : 's'}.
        </p>
        {missing > 0 && (
          <p className="warn">
            {missing} payee{missing === 1 ? ' has' : 's have'} no contact on file —
            add them below so you know how to pay them.
          </p>
        )}

        <div className="table-wrap">
          <table className="items">
            <thead>
              <tr>
                <th>Payee</th>
                <th>Owed</th>
                <th>Share</th>
                <th>Units</th>
                <th>Pay by</th>
                <th>Contact</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((row) => (
                <tr key={row.payee} className={row.has_contact ? '' : 'out-of-stock'}>
                  <td>{row.payee}</td>
                  <td className="num">${row.revenue.toFixed(2)}</td>
                  <td className="num">{row.share.toFixed(1)}%</td>
                  <td className="num">{row.units}</td>
                  <td>
                    {row.has_contact
                      ? `${methodLabel(row.payment_method)}${
                          row.payment_handle ? ` (${row.payment_handle})` : ''
                        }`
                      : 'No contact on file'}
                  </td>
                  <td className="small">{row.email || row.phone || row.address}</td>
                </tr>
              ))}
              {payouts.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted">
                    No sales in this range.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <div className="split">
        <section className="card">
          <h2>Contacts</h2>
          <div className="table-wrap">
            <table className="items">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Preferred payment</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => (
                  <tr key={contact.key ?? contact.name}>
                    <td>{contact.name}</td>
                    <td>{contact.kind}</td>
                    <td>
                      {methodLabel(contact.payment_method)}
                      {contact.payment_handle ? ` (${contact.payment_handle})` : ''}
                    </td>
                    <td>
                      <button type="button" className="ghost" onClick={() => edit(contact)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="ghost danger"
                        disabled={busy}
                        onClick={() => {
                          if (!window.confirm(`Delete the contact for ${contact.name}?`)) {
                            return;
                          }
                          void run(
                            () => api.deleteContact(contact.name),
                            'Contact deleted.',
                          );
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {contacts.length === 0 && (
                  <tr>
                    <td colSpan={4} className="muted">
                      No contacts yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="card">
          <h2>{editingName ? `Edit ${editingName}` : 'Add a contact'}</h2>

          <label>
            Name
            <input
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              placeholder="As it appears in an item's Payout to"
            />
          </label>

          <label>
            Type
            <select
              value={draft.kind}
              onChange={(event) =>
                setDraft({ ...draft, kind: event.target.value as api.Contact['kind'] })
              }
            >
              <option value="individual">Individual</option>
              <option value="business">Business</option>
            </select>
          </label>

          <label>
            Preferred payment
            <select
              value={draft.payment_method}
              onChange={(event) =>
                setDraft({ ...draft, payment_method: event.target.value })
              }
            >
              {api.PAYMENT_METHODS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <label>
            {HANDLE_PROMPTS[draft.payment_method] ?? 'Details'}
            <input
              value={draft.payment_handle}
              onChange={(event) =>
                setDraft({ ...draft, payment_handle: event.target.value })
              }
            />
          </label>

          <label>
            Email
            <input
              type="email"
              value={draft.email}
              onChange={(event) => setDraft({ ...draft, email: event.target.value })}
            />
          </label>

          <label>
            Phone
            <input
              value={draft.phone}
              onChange={(event) => setDraft({ ...draft, phone: event.target.value })}
            />
          </label>

          <label>
            Address
            <input
              value={draft.address}
              onChange={(event) => setDraft({ ...draft, address: event.target.value })}
              placeholder="Where to post a check, if relevant"
            />
          </label>

          <label>
            Notes
            <input
              value={draft.notes}
              onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
            />
          </label>

          <p className="warn small">
            Record only how to pay them — a Venmo handle, or who a check is made
            out to. Never account, routing or card numbers.
          </p>

          <button
            type="button"
            className="primary"
            disabled={busy || !draft.name.trim()}
            onClick={() =>
              void run(async () => {
                await api.saveContact({
                  ...draft,
                  original_name: editingName ?? undefined,
                });
                startNew();
              }, 'Contact saved.')
            }
          >
            {editingName ? 'Save changes' : 'Add contact'}
          </button>

          {editingName && (
            <button type="button" className="ghost" onClick={startNew}>
              Cancel
            </button>
          )}
        </aside>
      </div>
    </div>
  );
}
